import { useState } from "react";

import { Controller, useFieldArray, useFormContext } from "react-hook-form";

import { Delete as DeleteIcon, Edit as EditIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";

import { useAuth } from "@/features/authentication";
import { PermissionEnum } from "@/features/permissions";
import { ADD_ID, SIDE_NAME } from "../../product-variant-attributes";
import { LensProductVariantGroupForm } from "./lens-product-variant-group-form";
import { formatNumber } from "@/features/shared";

const LensProductVariantGroupsSection = () => {
  const { hasPermission } = useAuth();
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(null);

  const canUpdateRetailPrice = hasPermission(
    PermissionEnum.UPDATE_PRODUCT_RETAIL_PRICE
  );
  const canUpdateDistributorPrice = hasPermission(
    PermissionEnum.UPDATE_PRODUCT_DISTRIBUTOR_PRICE
  );

  const variantAttributes = watch("variantAttributes");
  const {
    fields: lensProductVariantGroups,
    append,
    update,
    remove,
  } = useFieldArray({
    control,
    name: "lensProductVariantGroups",
  });

  const checkedVariantAttributes = variantAttributes.filter(
    (attr) => attr.checked
  );

  return (
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      <Stack spacing={2}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{
            alignItems: { xs: "flex-start", sm: "center" }
          }}
        >
          <Typography variant="h6">Grup Varian</Typography>
          <Box>
            {variantAttributes.map((variantAttribute, index) => (
              <Controller
                key={variantAttribute.id}
                name={`variantAttributes.${index}.checked`}
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...field}
                        checked={field.value || false}
                        disabled={
                          variantAttribute.required ||
                          lensProductVariantGroups.length > 0
                        }
                        onChange={(e) => {
                          field.onChange(e.target.checked);

                          // Check the 3rd (Add) variantAttribute if the 4th (Side) one is checked
                          if (index === 3 && e.target.checked) {
                            setValue(`variantAttributes.2.checked`, true);
                          }

                          // Uncheck the 4th (Side) variantAttribute if the 3rd (Add) one is unchecked
                          if (index === 2 && !e.target.checked) {
                            setValue(`variantAttributes.3.checked`, false);
                          }
                        }}
                      />
                    }
                    label={variantAttribute.name}
                  />
                )}
              />
            ))}
          </Box>
        </Stack>
        {errors.lensProductVariantGroups && (
          <FormHelperText error>
            {errors.lensProductVariantGroups.message}
          </FormHelperText>
        )}
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            mb: 1
          }}>
          Untuk mengurutkan berdasarkan beberapa kolom, tahan tombol{" "}
          <Typography
            component="span"
            sx={{ fontWeight: "bold", fontFamily: "monospace" }}
          >
            Ctrl
          </Typography>{" "}
          atau{" "}
          <Typography
            component="span"
            sx={{ fontWeight: "bold", fontFamily: "monospace" }}
          >
            ⌘ Command
          </Typography>{" "}
          (untuk macOS) sambil mengklik header kolom.
        </Typography>
        <DataGridPro
          getRowHeight={() => "auto"}
          rows={lensProductVariantGroups}
          initialState={{
            sorting: {
              sortModel: [
                { field: "sph", sort: "desc" },
                { field: "cyl", sort: "desc" },
                { field: "add", sort: "asc" },
              ],
            },
          }}
          sortingOrder={["desc", "asc"]}
          columns={[
            ...checkedVariantAttributes.map((attr) => ({
              field: attr.name.toLowerCase(),
              headerName: attr.name,
              flex: 2,
              sortable: true,
              valueGetter: (value, row) => {
                if (attr.name === "Sph") return parseFloat(row.sphStart) || 0;
                if (attr.name === "Cyl") return parseFloat(row.cylStart) || 0;
                if (attr.name === "Add") return parseInt(row.addStart) || 0;
                if (attr.name === SIDE_NAME) return 0; // Fixed value for L/R
                return 0;
              },
              renderCell: (params) => {
                if (attr.name === "Sph")
                  return `${params.row.sphStart} s/d ${params.row.sphEnd || "-"}`;
                if (attr.name === "Cyl")
                  return `${params.row.cylStart} s/d ${params.row.cylEnd || "-"}`;
                if (attr.name === "Add")
                  return `${params.row.addStart} s/d ${params.row.addEnd || "-"}`;
                if (attr.name === SIDE_NAME) return "L/R";
                return "-";
              },
            })),
            {
              field: "stepSph",
              headerName: "Step Sph",
              flex: 1,
              sortable: false,
              type: "number",
              valueFormatter: (value) => formatNumber(value),
            },
            {
              field: "combination",
              headerName: "Comb.",
              flex: 1,
              type: "number",
              valueGetter: (value) => value || "-",
            },
            ...(canUpdateRetailPrice
              ? [
                  {
                    field: "retailPrice",
                    headerName: "Harga Retail (1 Pair)",
                    flex: 2,
                    type: "number",
                    valueFormatter: (value) => formatNumber(value),
                  },
                ]
              : []),
            ...(canUpdateDistributorPrice
              ? [
                  {
                    field: "distributorPrice",
                    headerName: "Harga Distributor (1 Pair)",
                    flex: 2,
                    type: "number",
                    valueFormatter: (value) => formatNumber(value),
                  },
                ]
              : []),
            {
              field: "actions",
              headerName: "Actions",
              width: 120,
              align: "center",
              headerAlign: "center",
              sortable: false,
              renderCell: (params) => {
                const rowIndex = lensProductVariantGroups.findIndex(
                  (group) => group === params.row
                );

                return (
                  <>
                    <IconButton onClick={() => setEditIndex(rowIndex)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton color="error" onClick={() => remove(rowIndex)}>
                      <DeleteIcon />
                    </IconButton>
                  </>
                );
              },
            },
          ]}
          disableRowSelectionOnClick
          hideFooterPagination
          hideFooterSelectedRowCount
        />
      </Stack>
      <Button
        variant="outlined"
        color="primary"
        sx={{ mt: 3 }}
        onClick={() => setIsAddDialogOpen(true)}
      >
        + Tambah
      </Button>
      {isAddDialogOpen && (
        <LensProductVariantGroupForm
          hasAdd={variantAttributes.find((attr) => attr.id === ADD_ID)?.checked}
          existingGroups={lensProductVariantGroups}
          onSubmit={(data) => append(data)}
          onClose={() => setIsAddDialogOpen(false)}
        />
      )}
      {editIndex !== null && (
        <LensProductVariantGroupForm
          hasAdd={variantAttributes.find((attr) => attr.id === ADD_ID)?.checked}
          existingGroups={lensProductVariantGroups}
          defaultValues={lensProductVariantGroups[editIndex]}
          onSubmit={(data) => update(editIndex, data)}
          onClose={() => setEditIndex(null)}
        />
      )}
    </Paper>
  );
};

export default LensProductVariantGroupsSection;
